import type { Ref } from "vue";
import { HttpClient } from "@/services/httpClient";
import { EnsureOpenDraftSalesInvoice } from "@/api/salesInvoiceApi";
import { applyBranchToDoc } from "@/helper/branchGuard";
import { lookupGuardianIdsBySearch, lookupPetIdsBySearch } from "@/api/frappeLookupApi";
import { Controllers } from "@/type/api";
import type {
  VisitDetail,
  VisitFilterParam,
  VisitHistoryItem,
  VisitListItem,
  VisitUpsertPayload,
} from "@/type/healthcare";
import type { SalesInvoiceEnsureDraftResult } from "@/type/salesInvoice";
import {
  mapVisitDetail,
  mapVisitHistoryItem,
  mapVisitListItem,
} from "@/api/healthcareApi.utils";
import { HEALTHCARE_USE_MOCKS, VISIT_CONSULTATION_FIELDS_LIVE } from "@/modules/healthcare/config";
import {
  mockGetVisitById,
  mockGetVisitHistory,
  mockGetVisits,
  mockSaveVisit,
} from "@/modules/healthcare/mock";
import {
  fetchFrappeDocCount,
  normalizeFrappeListResult,
  withFrappeLoading,
  type FrappeListResult,
} from "@/services/frappe/list";
import {
  buildFrappeLikeSearchFilters,
  serializeFrappeFilters,
  type FrappeFilterClause,
} from "@/services/frappe/filters";
import {
  CreateVisit as CreateLegacyVisit,
  GetVisits as GetLegacyVisits,
  UpdateVisit as UpdateLegacyVisit,
} from "@/api/visitApi";
import { GetWorkspaceRecord, type WorkspaceRecordDetail } from "@/api/workspaceApi";

const healthcareVisitService = new HttpClient(Controllers.vetVisit);
export const VET_VISIT_DOCTYPE = "Vet Visit";
// Used to turn a `doctor` link id into the practitioner's real name for the visit history.
const practitionerService = new HttpClient(Controllers.doctor);
const PRACTITIONER_DOCTYPE = "Healthcare Practitioner";
const DEFAULT_VET_VISIT_STATUS = "Draft";

const VET_VISIT_RESOURCE_FIELDS = JSON.stringify([
  // Gated: an unknown column fails the whole list query — see VISIT_CONSULTATION_FIELDS_LIVE.
  ...(VISIT_CONSULTATION_FIELDS_LIVE ? ["consultation_seconds"] : []),
  "name",
  "status",
  "priority",
  "visit_type",
  "visit_datetime",
  "case_sheet",
  "animal_patient",
  "animal_patient.pet_name",
  "animal_patient.pet_image",
  "animal_patient.animal_species",
  // animal_type is the Dog/Cat signal the pet avatar keys on; animal_species is the
  // biological class ("Mammal") and cannot produce a species icon. Without this the
  // whole list rendered generic paws while the DETAIL page (which fetches ["*"]) was
  // correct — the difference was this field list, not the backend.
  "animal_patient.animal_type",
  "animal_patient.breed",
  "guardian",
  "guardian_name",
  "guardian.full_name",
  "guardian.phone",
  "doctor",
  "doctor_name",
  // NOTE: no `doctor.practitioner_name` here on purpose. A dotted link column is one more field
  // that can trigger "Field not permitted in query" and take the ENTIRE request down (that is
  // exactly what `title` did to the Pet Care Episode query). `resolvePractitionerNames` below
  // resolves the name in a separate, batched request instead, so a permission change on the
  // practitioner doctype can never blank the whole visit history.
  "case_summary",
  "overview",
  "examination_notes",
  "diagnosis",
  "treatment_plan",
  "billing_status",
  "creation",
  "modified",
]);

// Columns the list search box matches against. Every visible table column is covered:
// pet and owner are link fields resolved through the id lookups in GetHealthcareVisits,
// the rest are plain columns on Vet Visit itself.
const VET_VISIT_SEARCH_FIELDS = [
  "name",
  "case_sheet",
  "guardian_name",
  "doctor_name",
  "status",
  "billing_status",
  "case_summary",
];

function buildVisitResourceParams(filter: VisitFilterParam, safe = false) {
  const filters: FrappeFilterClause[] = safe ? [] : [...(filter.filters ?? [])];

  if (!safe) {
    if (filter.status)
      filters.push([VET_VISIT_DOCTYPE, "status", "=", filter.status]);
    if (filter.practitioner)
      filters.push([VET_VISIT_DOCTYPE, "doctor", "=", filter.practitioner]);
    if (filter.petId)
      filters.push([VET_VISIT_DOCTYPE, "animal_patient", "=", filter.petId]);
    if (filter.guardianId)
      filters.push([VET_VISIT_DOCTYPE, "guardian", "=", filter.guardianId]);
  }

  const orFilters = safe
    ? undefined
    : serializeFrappeFilters(
        [
          ...(filter.orFilters ?? []),
          ...buildFrappeLikeSearchFilters(
            VET_VISIT_SEARCH_FIELDS,
            filter.search,
            VET_VISIT_DOCTYPE,
          ),
        ],
      );

  return {
    limit_start: filter.limit_start ?? 0,
    limit_page_length: filter.limit_page_length ?? 20,
    order_by: filter.order_by ?? "modified desc",
    filters: serializeFrappeFilters(filters),
    or_filters: orFilters,
  };
}

async function getVisitsFromResource(filter: VisitFilterParam) {
  const params = buildVisitResourceParams(filter);

  try {
    const response = await healthcareVisitService.get<{ data?: any[] }>(
      "",
      params,
      {},
      VET_VISIT_RESOURCE_FIELDS,
    );
    const total = await fetchFrappeDocCount(
      healthcareVisitService,
      VET_VISIT_DOCTYPE,
      params.filters,
      params.or_filters,
    ).catch(() => response?.data?.length ?? 0);
    const normalized = normalizeFrappeListResult(response, total);

    return {
      data: await mapVisitListRows(normalized.data),
      total: normalized.total,
    };
  } catch (error) {
    console.warn("Falling back to minimal Vet Visit list:", error);
    const safeParams = buildVisitResourceParams(filter, true);
    const response = await healthcareVisitService.get<{ data?: any[] }>(
      "",
      safeParams,
      {},
      JSON.stringify(["*"]),
    );
    const normalized = normalizeFrappeListResult(response);

    return {
      data: await mapVisitListRows(normalized.data),
      total: normalized.total,
    };
  }
}

/**
 * ⚠️ A CLINICAL FIELD MAY BE A CONTAINER, NOT A STRING.
 *
 * `clinical.assessment` and `clinical.instructions` arrive as
 * `{ selected, items, note }` and are ALWAYS present — non-empty as objects even
 * when the clinician wrote nothing. Handing one to `mapVisitDetail` would call
 * `String(value)` on it (healthcareApi.utils.ts:74) and render the literal text
 * `[object Object]`, and any truthiness check downstream would paint a populated
 * section over an empty one. Flatten to real text, or to null.
 */
function clinicalText(value: unknown): string | null {
  if (value === null || value === undefined)
    return null;
  if (typeof value === "string")
    return value.trim() || null;
  if (Array.isArray(value))
    return value.map(clinicalText).filter(Boolean).join(", ") || null;

  if (typeof value === "object") {
    const container = value as Record<string, unknown>;
    const parts: string[] = [];
    for (const key of ["selected", "items"]) {
      const entry = container[key];
      if (Array.isArray(entry)) {
        parts.push(...entry
          .map(item => (item && typeof item === "object"
            ? clinicalText((item as Record<string, unknown>).label
              ?? (item as Record<string, unknown>).name
              ?? (item as Record<string, unknown>).title)
            : clinicalText(item)))
          .filter((item): item is string => Boolean(item)));
      }
      else if (entry) {
        const text = clinicalText(entry);
        if (text)
          parts.push(text);
      }
    }
    const note = clinicalText(container.note);
    if (note)
      parts.push(note);

    return parts.join(" \u2014 ") || null;
  }

  return String(value).trim() || null;
}

/**
 * `pet_app.api.workspace.get_record` -> `VisitDetail`.
 *
 * ⚠️ EMPTY IS NOT FAILURE. `orders` and `linked_records` are non-empty on only
 * 461 of 1550 visits, and `clinical.examination` is null on ALL of them because
 * nothing has ever been written to that column. A visit that genuinely has no
 * orders is a complete, successful read — it must render an empty state, never a
 * retry, and never a fall through to another endpoint.
 */
function visitDetailFromWorkspaceRecord(
  record: WorkspaceRecordDetail,
  visitId: string,
): VisitDetail {
  const summary = (record.summary ?? {}) as Record<string, any>;
  const clinical = (record.clinical ?? {}) as Record<string, any>;

  return mapVisitDetail({
    ...summary,
    name: summary.name || record.name || visitId,
    status: summary.status ?? record.status,
    priority: summary.priority ?? record.priority,
    // ⚠️ THE VISIT'S OWN DOCTOR IS `primary_practitioner`. `mapVisitDetail`
    // does not read that alias, and the old fallback carried no id at all —
    // which is how `linkCaseSheetViaVisitSave` came to save a null practitioner.
    practitioner_id: summary.primary_practitioner ?? summary.practitioner ?? summary.doctor ?? null,
    practitioner_name: summary.practitioner_name ?? summary.doctor_name ?? null,
    practitioner_image: summary.practitioner_image ?? summary.doctor_image ?? null,
    case_sheet_id: summary.case_sheet_id ?? null,
    visit_datetime: summary.visit_datetime ?? null,

    pet: record.pet,
    guardian: record.guardian,

    chief_complaint: clinicalText(clinical.chief_complaint ?? clinical.chiefComplaint),
    intake_summary: clinicalText(clinical.intake_summary ?? clinical.intakeSummary),
    overview: clinicalText(clinical.overview),
    examination: clinicalText(clinical.examination),
    assessment: clinicalText(clinical.assessment ?? clinical.diagnosis),
    plan: clinicalText(clinical.plan),
    instructions: clinicalText(clinical.instructions),

    // Genuinely empty arrays stay empty arrays.
    orders: record.orders ?? [],
    timeline: record.timeline ?? [],
    billing: record.billing ?? null,
  });
}

/** The app endpoint. Covered by the read relaxation; resolves any visit by key. */
async function getVisitByIdFromWorkspace(visitId: string) {
  const record = await GetWorkspaceRecord({ source_type: "Vet Visit", name: visitId });

  return record ? visitDetailFromWorkspaceRecord(record, visitId) : null;
}

async function getVisitByIdFromResource(visitId: string) {
  const response = await healthcareVisitService.get<any>(
    `/${encodeURIComponent(visitId)}`,
    {},
    {},
    JSON.stringify(["*"]),
  );
  const payload = response?.data ?? response;

  return payload ? mapVisitDetail(payload) : null;
}

async function getVisitHistoryFromResource(petId?: string | null, visitId?: string | null) {
  if (!petId)
    return [];

  const params = buildVisitResourceParams({
    petId,
    limit_start: 0,
    // Was hardcoded to 20, which silently truncated a long-tenured pet's history with no way to
    // page. The timeline pages client-side, so fetch a fuller window.
    limit_page_length: 100,
    order_by: "modified desc",
  });

  // ⚠️ THE FIELD LIST CAN SINK THE WHOLE REQUEST. Frappe rejects a list query outright when any
  // requested column is not permitted or does not exist ("Field not permitted in query", HTTP
  // 417) — it does not just omit that column. Because `GetHealthcareVisitHistory` swallows errors
  // into `[]`, ONE bad field made the entire history render as "no history". `getVisitsFromResource`
  // already guards this way; the history path did not. Retry with `["*"]` so a single unsupported
  // column degrades to a slightly heavier read instead of an empty timeline.
  let rows: any[] = [];
  try {
    const response = await healthcareVisitService.get<{ data?: any[] }>(
      "",
      params,
      {},
      VET_VISIT_RESOURCE_FIELDS,
    );

    rows = response?.data ?? [];
  }
  catch (error) {
    console.warn("Falling back to unrestricted Vet Visit history fields:", error);

    const response = await healthcareVisitService.get<{ data?: any[] }>(
      "",
      params,
      {},
      JSON.stringify(["*"]),
    );

    rows = response?.data ?? [];
  }

  const items = rows
    .filter(item => String(item?.name ?? "") !== String(visitId ?? ""))
    .map(mapVisitHistoryItem);

  return resolvePractitionerNames(
    items,
    rows,
    item => item.practitioner,
    (item, practitioner) => ({ ...item, practitioner }),
  );
}

/**
 * One batched `Healthcare Practitioner` read for a page's worth of doctor link ids.
 *
 * Shared by the visit history and the visit list so neither ever issues a request per doctor.
 * Returns an empty map on failure — a missing name always means "leave the item as it was",
 * never an error worth putting in front of the user.
 */
async function fetchPractitionerNames(ids: Set<string>): Promise<Map<string, string>> {
  if (!ids.size)
    return new Map();

  try {
    const response = await practitionerService.get<{ data?: any[] }>(
      "",
      {
        filters: JSON.stringify([[PRACTITIONER_DOCTYPE, "name", "in", [...ids]]]),
        limit_page_length: ids.size,
      },
      {},
      JSON.stringify(["name", "practitioner_name"]),
      { headers: { "X-Suppress-Action-Loader": "1" } },
    );

    return new Map(
      (response?.data ?? []).map(row => [
        String(row?.name ?? "").trim(),
        String(row?.practitioner_name ?? "").trim(),
      ]),
    );
  }
  catch (error) {
    console.warn("Could not resolve practitioner names:", error);

    return new Map();
  }
}

/**
 * Replaces any practitioner slot still holding a link id with the practitioner's real name.
 *
 * Two paths lead here with an unresolved doctor: the row genuinely has no `doctor_name`, or a
 * field-list fallback dropped the dotted `doctor.practitioner_name` (a `["*"]` read returns
 * parent columns only). Either way the screen was showing "HCP-00012" where a person's name
 * belongs. One batched lookup covers every distinct doctor on the page; on failure the items
 * are returned unchanged.
 *
 * ⚠️ A SLOT CAN BE NON-EMPTY AND STILL BE AN ID, so a null check alone is not enough.
 * `mapVisitListItem`'s alias chain ends at the raw `doctor` link field, which means a visit with
 * no `doctor_name` yields `practitionerName === "HCP-00012"` — truthy, and previously waved
 * straight through. Comparing against the row's own `doctor` value is the only reliable way to
 * tell a name from an id, so that is the test rather than a shape/prefix guess.
 */
async function resolvePractitionerNames<T>(
  items: T[],
  rows: any[],
  read: (item: T) => string | null | undefined,
  write: (item: T, name: string) => T,
): Promise<T[]> {
  const doctorIdAt = (index: number) => String(rows[index]?.doctor ?? "").trim();

  const isUnresolved = (item: T, index: number) => {
    const doctorId = doctorIdAt(index);

    if (!doctorId)
      return false;

    const current = String(read(item) ?? "").trim();

    return !current || current === doctorId;
  };

  const unresolved = new Set<string>();

  items.forEach((item, index) => {
    if (isUnresolved(item, index))
      unresolved.add(doctorIdAt(index));
  });

  const names = await fetchPractitionerNames(unresolved);

  if (!names.size)
    return items;

  return items.map((item, index) => {
    if (!isUnresolved(item, index))
      return item;

    const resolved = names.get(doctorIdAt(index));

    return resolved ? write(item, resolved) : item;
  });
}

/**
 * `mapVisitListItem` + the id→name repair, kept in one place so the primary and `["*"]` fallback
 * reads in `getVisitsFromResource` cannot drift apart. The fallback path needs this MORE, not
 * less: a `["*"]` read returns parent columns only, so `doctor.practitioner_name` is never in it.
 */
async function mapVisitListRows(rows: any[]) {
  const items = rows.map(mapVisitListItem);

  return resolvePractitionerNames(
    items,
    rows,
    item => item.practitionerName,
    (item, practitionerName) => ({ ...item, practitionerName }),
  );
}

function buildVetVisitResourcePayload(payload: VisitUpsertPayload) {
  return {
    case_sheet: payload.caseSheetId || null,
    animal_patient: payload.petId || null,
    guardian: payload.guardianId || null,
    doctor: payload.practitionerId || null,
    status: normalizeVetVisitStatus(payload.status),
    priority: payload.priority || null,
    case_summary: payload.chiefComplaint || null,
    intake_summary: payload.intakeSummary || null,
    overview: payload.overview || null,
    examination_notes: payload.examination || null,
    assessment: payload.assessment || null,
    treatment_plan: payload.plan || null,
    instructions: payload.instructions || null,
    billing_status: payload.billing.status || null,
  };
}

function normalizeVetVisitStatus(status?: string | null) {
  const value = String(status ?? "").trim();

  if (["Draft", "In Progress", "Completed", "Cancelled", "Follow-up Needed"].includes(value))
    return value;

  if (value === "In Consultation")
    return "In Progress";

  if (value === "Closed")
    return "Completed";

  return DEFAULT_VET_VISIT_STATUS;
}

async function saveVisitToResource(payload: VisitUpsertPayload) {
  const body = buildVetVisitResourcePayload(payload);
  const response = payload.id
    ? await healthcareVisitService.put<any, typeof body>(
        undefined,
        encodeURIComponent(payload.id),
        body,
      )
    // Insert only: stamp the clinic when the backend can't infer it.
    : await healthcareVisitService.post<any, typeof body>(await applyBranchToDoc("Vet Visit", body));
  const normalized = response?.data ?? response;
  const visitId = normalized?.name || normalized?.visit_id || payload.id;

  return visitId ? { visitId: String(visitId) } : null;
}

export interface SaveHealthcareVisitResult {
  visitId: string;
  /**
   * The customer's open Draft this visit was attached to (`created: false`), or the one
   * opened for it (`created: true`). Null when the visit carries no customer, when the
   * visit was an update rather than a create, or when the draft step failed.
   */
  draftInvoice: SalesInvoiceEnsureDraftResult | null;
  /** Non-null means the draft-invoice step FAILED. The visit itself saved successfully. */
  draftInvoiceError: string | null;
}

async function ensureDraftInvoiceForCreatedVisit(
  payload: VisitUpsertPayload,
  visitId: string,
): Promise<{ invoice: SalesInvoiceEnsureDraftResult | null; error: string | null }> {
  const customer = String(payload.customerId ?? "").trim();

  let resolvedCustomer = customer;

  if (!resolvedCustomer) {
    try {
      const detail = await getVisitByIdFromResource(visitId);
      resolvedCustomer = String(detail?.guardian?.customerId ?? "").trim();
    } catch {
      resolvedCustomer = "";
    }
  }

  // Genuinely nothing to bill against — not a failure.
  if (!resolvedCustomer)
    return { invoice: null, error: null };

  try {
    return {
      invoice: await EnsureOpenDraftSalesInvoice({ value: false } as Ref<boolean>, resolvedCustomer),
      error: null,
    };
  } catch (error: any) {
    // The visit is already saved by the time we get here, so billing must not retroactively
    // fail it. But it must not disappear either: the backend's message rides back on the
    // result so the calling screen can warn instead of showing an unqualified success.
    console.error("Draft sales invoice could not be prepared for visit", visitId, error);

    return {
      invoice: null,
      error: String(error?.message ?? error ?? "").trim() || "draft-invoice-failed",
    };
  }
}

/**
 * Save the visit, falling back to the legacy endpoint if the resource save fails.
 * Extracted so the draft-invoice step can run strictly AFTER this settles — see
 * `SaveHealthcareVisit`.
 */
async function saveVisitWithLegacyFallback(
  loading: Ref<boolean>,
  payload: VisitUpsertPayload,
): Promise<{ visitId: string } | null> {
  try {
    return await saveVisitToResource(payload);
  } catch (error: any) {
    console.warn("Falling back to legacy visit save:", error?.message ?? error);

    const legacyPayload = buildLegacyVisitPayload(payload);
    // CreateLegacyVisit (visitApi.CreateVisit) now re-throws instead of returning null. Keep
    // SaveHealthcareVisit's null-on-failure contract here so its many callers are unaffected;
    // the backend's reason is already surfaced by the httpClient interceptor. (Fully surfacing
    // visit-create failures to every UI caller is a separate, larger change.)
    let ok: string | boolean | null;
    try {
      ok = payload.id
        ? await UpdateLegacyVisit(loading, payload.id, legacyPayload)
        : await CreateLegacyVisit(loading, legacyPayload);
    } catch (legacyError: any) {
      console.error(legacyError?.message ?? legacyError);

      return null;
    }

    if (!ok)
      return null;

    return {
      visitId: typeof ok === "string" ? ok : payload.id || "",
    };
  }
}

function summarizeVisitPayload(payload: VisitUpsertPayload) {
  return [
    payload.chiefComplaint ? `Chief Complaint: ${payload.chiefComplaint}` : "",
    payload.overview ? `Overview: ${payload.overview}` : "",
    payload.examination ? `Examination: ${payload.examination}` : "",
    payload.assessment ? `Assessment: ${payload.assessment}` : "",
    payload.plan ? `Plan: ${payload.plan}` : "",
    payload.instructions ? `Instructions: ${payload.instructions}` : "",
  ]
    .filter(Boolean)
    .join("\n\n");
}

function buildLegacyVisitPayload(payload: VisitUpsertPayload) {
  return {
    custom_guardian_id: payload.guardianId || "",
    custom_pet_id: payload.petId || "",
    encounter_comment: summarizeVisitPayload(payload),
    custom_total_amount: payload.billing.total ?? undefined,
    custom_services: [],
  };
}

export async function GetHealthcareVisits(
  loading: Ref<boolean>,
  filter: VisitFilterParam,
): Promise<FrappeListResult<VisitListItem> | null> {
  return withFrappeLoading(loading, async () => {
    if (HEALTHCARE_USE_MOCKS) {
      const response = await mockGetVisits(filter);

      return {
        data: response.data.map(mapVisitListItem),
        total: response.total,
      };
    }

    const [guardianMatches, petMatches] = await Promise.all([
      lookupGuardianIdsBySearch(filter.search),
      lookupPetIdsBySearch(filter.search),
    ]);
    const linkSearchFilters: FrappeFilterClause[] = [
      ...(guardianMatches.length
        ? [[VET_VISIT_DOCTYPE, "guardian", "in", guardianMatches] as FrappeFilterClause]
        : []),
      ...(petMatches.length
        ? [[VET_VISIT_DOCTYPE, "animal_patient", "in", petMatches] as FrappeFilterClause]
        : []),
    ];
    const filterWithLinkSearch: VisitFilterParam = linkSearchFilters.length
      ? {
          ...filter,
          orFilters: [...(filter.orFilters ?? []), ...linkSearchFilters],
        }
      : filter;

    try {
      return await getVisitsFromResource(filterWithLinkSearch);
    } catch (error: any) {
      console.warn("Falling back to legacy visit list:", error?.message ?? error);

      const legacy = await GetLegacyVisits(loading, {
        search: filter.search,
        limit_start: filter.limit_start,
        limit_page_length: filter.limit_page_length,
        order_by: filter.order_by,
      });

      if (!legacy)
        return null;

      return {
        data: legacy.data.map(item =>
          mapVisitListItem({
            ...item,
            case_sheet_no: null,
            status: DEFAULT_VET_VISIT_STATUS,
            billing_status: null,
            practitioner_name: item.practitioner_name || item.practitioner,
          }),
        ),
        total: legacy.total,
      };
    }
  });
}

/**
 * Read one visit.
 *
 * ⚠️ THE APP ENDPOINT IS PRIMARY. `pet_app.api.workspace.get_record` is covered
 * by the backend read relaxation and resolves any Vet Visit by primary key; the
 * generic `/api/resource/Vet Visit/{id}` route is still refused for a colleague's
 * visit, so it can only be a fallback for the caller's own records.
 *
 * ⚠️ THERE IS NO DEGRADED SHAPE ANY MORE. This used to fall back to
 * `pet_app.api.visit.get_visit` and hand-build a `VisitDetail` with `orders: []`
 * and billing collapsed to a total — it logged to the console and RENDERED
 * SUCCESSFULLY, so a doctor saw a visit with no orders and no error. Worse, it
 * fed `linkCaseSheetViaVisitSave`, which saved the resulting null practitioner
 * back onto the record. Both attempts failing now THROWS.
 */
export async function GetHealthcareVisitById(
  loading: Ref<boolean>,
  visitId: string,
): Promise<VisitDetail | null> {
  return withFrappeLoading(loading, async () => {
    if (HEALTHCARE_USE_MOCKS)
      return mockGetVisitById(visitId);

    try {
      return await getVisitByIdFromWorkspace(visitId);
    }
    catch (workspaceError: any) {
      // Only a THROWN error reaches here. A visit with no orders, no timeline
      // and empty clinical text is a successful read and is returned above.
      try {
        return await getVisitByIdFromResource(visitId);
      }
      catch (resourceError: any) {
        // ⚠️ SURFACE IT. Returning null renders an empty shell just as
        // silently as the old degraded shape did.
        throw workspaceError ?? resourceError;
      }
    }
  });
}

export async function GetHealthcareVisitHistory(
  loading: Ref<boolean>,
  petId?: string | null,
  visitId?: string | null,
): Promise<VisitHistoryItem[]> {
  return withFrappeLoading(loading, async () => {
    if (HEALTHCARE_USE_MOCKS)
      return mockGetVisitHistory(petId, visitId);

    try {
      return await getVisitHistoryFromResource(petId, visitId);
    } catch (error: any) {
      // The Vet Visit resource is the only history source. The old Patient Encounter fallback was
      // removed — that doctype isn't installed on this site, so it 404'd on every load (leftover
      // from an earlier design). Best-effort: an empty list keeps the non-throwing contract callers
      // rely on rather than swapping one dead request for another.
      //
      // ⚠️ THIS RETURN IS INDISTINGUISHABLE FROM "the pet has no visits" AND THAT HAS ALREADY
      // COST A DEBUGGING SESSION. Six callers depend on the non-throwing array contract, so the
      // shape stays — but the failure is logged as an ERROR with the real message rather than a
      // quiet warn, because the visible symptom ("Case History is empty") points nowhere near
      // this line. If this contract is ever revisited, returning `null` for failure and `[]` for
      // genuinely-empty is the correct fix; all six call sites would need updating together.
      console.error(
        "[healthcareVisitApi] Visit history request FAILED — rendering as empty history:",
        error?.message ?? error,
      );

      return [];
    }
  });
}

export async function SaveHealthcareVisit(
  loading: Ref<boolean>,
  payload: VisitUpsertPayload,
): Promise<SaveHealthcareVisitResult | null> {
  return withFrappeLoading(loading, async () => {
    if (HEALTHCARE_USE_MOCKS) {
      const mocked = await mockSaveVisit(payload);

      return mocked ? { ...mocked, draftInvoice: null, draftInvoiceError: null } : null;
    }

    const result = await saveVisitWithLegacyFallback(loading, payload);

    if (!result?.visitId)
      return null;

    // AFTER the save has settled — NEVER inside its try. The draft-invoice call used to sit
    // inside the resource-save try block, so a billing failure was caught by the visit-save
    // catch and re-ran the ENTIRE save down the legacy path: one click, two Vet Visits.
    const draft = payload.id
      ? { invoice: null, error: null }
      : await ensureDraftInvoiceForCreatedVisit(payload, result.visitId);

    return {
      visitId: result.visitId,
      draftInvoice: draft.invoice,
      draftInvoiceError: draft.error,
    };
  });
}
